/* eslint-disable react/no-children-prop */
import React, {
  ComponentType,
  useState
} from 'react'
import { connect } from 'react-redux'
import {
  Accordion,
  Grid
} from 'semantic-ui-react'
import ReactMarkdown from 'react-markdown'
// Local
import { CONDITION } from '../../../constant'
import { conditionClaimSource } from '../../common/conditionUtils'
import ConditionAddressedDate from '../common/conditionAddressedDate'
import ConditionEvidence from '../common/conditionEvidence'
import ClinicalAdminConditionHeaderTitle from '../clinicalAdmin/clinicalAdminConditionHeaderTitle'
import { EncounterConditionDisplay } from '../../authenticated/types'
import If from '../../common/if'
import { RootState } from '../../../store'

const SafeGrid = Grid as unknown as ComponentType<any>

/**
 * Provides a component that renders a Patient Snapshot condition view
 *
 * The Patient Snapshot view of conditions is very similar to the Clinical Admin
 * view of conditions and may use some Clinical Admin components
 *
 * @param condition Condition entity
 * @param mode View mode. Options: "smart", null. Default: null
 * @param active If true, display condition expanded. Default: false
 * @param hccChild True if condition is a member of an HCC display group
 * @param hccAddressed True if condition is a member of an HCC display group
 *        and the group is marked as addressed
 * @param displayIndex Condition display index in its display group
 * @param onClickTitle Callback for condition accordion title click
 *        Arguments: condition
 * @param onConditionAction Callback for condition action
 *        Arguments action, condition
 */

const mapStateToProps = (state: RootState) => ({
  currentUser: state.user
})

type ReduxStateProps = ReturnType<typeof mapStateToProps>

type Props = ReduxStateProps & {
  condition: EncounterConditionDisplay
  displayIndex?: number | undefined
  hccChild: boolean
  onConditionAction: (
    action: keyof typeof CONDITION.ACTION,
    condition: EncounterConditionDisplay | string
  ) => void
}

function PatientSnapshotCondition(
  props: Props
): JSX.Element {
  const {
    condition,
    currentUser,
    displayIndex,
    hccChild: isHCCChild,
    onConditionAction
  } = props
  const [
    isActiveState,
    setIsActiveState
  ] = useState<boolean>(true)

  const onClickTitle = (): void => {
    if (isActiveState === true) {
      // deactivate condition
      setIsActiveState(false)
      onConditionAction(
        CONDITION.ACTION.DEACTIVATE,
        condition
      )
    } else {
      // activate condition
      setIsActiveState(true)
      onConditionAction(
        CONDITION.ACTION.ACTIVATE,
        condition
      )
      onConditionAction(
        CONDITION.ACTION.READ,
        condition
      )
    }
  }

  // const onClickDismiss = (): void => {
  //   if (condition.conditionCode === CONDITION.CODE.DISMISSED) {
  //     onConditionAction(
  //       CONDITION.ACTION.MARK_OPEN,
  //       condition
  //     )
  //   } else {
  //     onConditionAction(
  //       CONDITION.ACTION.MARK_DISMISSED,
  //       condition
  //     )
  //   }
  // }

  const claimSource = conditionClaimSource(condition)
  const clazz = displayIndex && displayIndex === 0
    ? 'first-accordion' : ''

  return (
    <div className="condition">
      <Accordion.Title
        active={ isActiveState }
        onClick={ onClickTitle }
        index={ condition.id }
        className={ clazz }
      >
        <ClinicalAdminConditionHeaderTitle
          condition={ condition }
          hccChild={ isHCCChild }
          active={ isActiveState }
        />
      </Accordion.Title>
      <Accordion.Content active={ isActiveState }>
        <SafeGrid
          className="internal-condition"
          columns={ 1 }
          divided
          stackable
        >
          <Grid.Row className="first-row">
            <Grid.Column>
              <div>
                <ConditionEvidence
                  condition={ condition }
                  currentUser={ currentUser }
                />
                <ReactMarkdown
                  children={ condition.content || '' }
                  linkTarget="_blank"
                />
                <ConditionAddressedDate condition={ condition } />
              </div>
            </Grid.Column>
          </Grid.Row>
          <If isTrue={ !!claimSource }>
            <Grid.Row>
              <Grid.Column textAlign="center">
                <span className="footnote">
                  Initial source of condition: { claimSource }
                </span>
              </Grid.Column>
            </Grid.Row>
          </If>
        </SafeGrid>
      </Accordion.Content>
    </div>
  )
}

export default connect(mapStateToProps)(PatientSnapshotCondition)
